import React from 'react';
import type { DateTime as Interface } from '../../design/components';
import {
  type DatePickerVariant,
  getInputType,
  getTimezoneAbbr,
  utcToLocal,
  localToUtc,
  formatDisplay,
} from '../shared/datetime';

export const DateTime: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder,
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
  variant = 'date',
  range,
  min,
  max,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const v = variant as DatePickerVariant;
  const strValue = typeof value === 'string' ? value : '';
  const showTz = v === 'date-time' || v === 'time';

  if (inline) {
    return (
      <span style={{ display: hidden ? 'none' : undefined }}>
        {formatDisplay(strValue, v, range)}
      </span>
    );
  }

  // Range mode: two inputs of the same type
  if (range) {
    const [startStr, endStr] = strValue.split('/');
    const inputType = getInputType(v);

    const handleStart = (e: React.ChangeEvent<HTMLInputElement>) => {
      const start = localToUtc(e.target.value, v);
      const end = endStr || '';
      setValue(end ? `${start}/${end}` : start);
    };

    const handleEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
      const start = startStr || '';
      const end = localToUtc(e.target.value, v);
      setValue(start ? `${start}/${end}` : end);
    };

    return (
      <div style={{ marginBottom: '1rem', display: hidden ? 'none' : undefined }}>
        <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
          {name}
          {required && <span style={{ color: 'red' }}> *</span>}
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type={inputType}
            id={`${name}-start`}
            value={utcToLocal(startStr || '', v)}
            onChange={handleStart}
            disabled={disabled}
            required={required}
            min={min ? String(min) : undefined}
            max={max ? String(max) : undefined}
            style={inputStyle(!!error, !!disabled, !!readonly)}
            {...(v === 'year' ? { minLength: 4, maxLength: 4 } : {})}
            {...(readonly ? { tabIndex: -1, onMouseDown: (e: React.MouseEvent) => e.preventDefault() } : {})}
          />
          <span>–</span>
          <input
            type={inputType}
            id={`${name}-end`}
            value={utcToLocal(endStr || '', v)}
            onChange={handleEnd}
            disabled={disabled}
            required={required}
            min={min ? String(min) : undefined}
            max={max ? String(max) : undefined}
            style={inputStyle(!!error, !!disabled, !!readonly)}
            {...(v === 'year' ? { minLength: 4, maxLength: 4 } : {})}
            {...(readonly ? { tabIndex: -1, onMouseDown: (e: React.MouseEvent) => e.preventDefault() } : {})}
          />
          {showTz && <span style={{ color: '#666', fontSize: '13px' }}>{getTimezoneAbbr()}</span>}
        </div>
        {(error || description) && (
          <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
            {error || description}
          </small>
        )}
      </div>
    );
  }

  // Single mode
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(localToUtc(e.target.value, v));
  };

  return (
    <div style={{ marginBottom: '1rem', display: hidden ? 'none' : undefined }}>
      <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
        {name}
        {required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type={getInputType(v)}
          id={name}
          name={name}
          value={utcToLocal(strValue, v)}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          min={min ? String(min) : undefined}
          max={max ? String(max) : undefined}
          style={inputStyle(!!error, !!disabled, !!readonly)}
          {...(v === 'year' ? { minLength: 4, maxLength: 4 } : {})}
          {...(readonly ? { tabIndex: -1, onMouseDown: (e: React.MouseEvent) => e.preventDefault() } : {})}
        />
        {showTz && (
          <span style={{ color: '#666', fontSize: '13px' }}>{getTimezoneAbbr()}</span>
        )}
      </div>
      {(error || description) && (
        <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
          {error || description}
        </small>
      )}
    </div>
  );
};

function inputStyle(hasError: boolean, isDisabled: boolean, isReadonly: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '8px 12px',
    fontSize: '16px',
    border: hasError ? '1px solid red' : '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    backgroundColor: isDisabled ? '#f5f5f5' : isReadonly ? '#f9f9f9' : '#fff',
  };
}
